import { useEffect, useRef } from 'react';
import { create } from 'zustand';

interface ISignWellEmbed {
  url: string;
  events: {
    completed: (e: { id: string; url: string }) => void;
    closed: (e: { id: string }) => void;
    iframeLoaded?: (e: { id: string }) => void;
    documentLoaded?: (e: { id: string }) => void;
    error?: () => void;
  };
  open: () => void;
  close: () => void;
}

interface SignWellScriptState {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  loadScript: () => void;
}

export const useSignWellScriptStore = create<SignWellScriptState>((set, get) => ({
  isLoaded: false,
  isLoading: false,
  error: null,
  loadScript: () => {
    if (get().isLoaded || get().isLoading) return;
    set({ isLoading: true, error: null });

    const existingScript = document.querySelector('script[src*="signwell.com/assets/embedded.js"]');
    if (existingScript) {
      set({ isLoaded: true, isLoading: false });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://static.signwell.com/assets/embedded.js';
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => set({ isLoaded: true, isLoading: false });
    script.onerror = () =>
      set({ isLoaded: false, isLoading: false, error: 'Error loading script' });

    document.body.appendChild(script);
  }
}));

export const useSignWellScript = () => {
  const { isLoaded, isLoading, error, loadScript } = useSignWellScriptStore();
  const signWellEmbedRef = useRef<ISignWellEmbed | null>(null);
  const isContractLoadedRef = useRef(false);

  useEffect(() => {
    loadScript();
  }, [loadScript]);

  const openSignWell = (
    signUrl: string,
    options?: { onComplete?: () => void; onClose?: () => void }
  ) => {
    if (!isLoaded || !window.SignWellEmbed || isContractLoadedRef.current) return;

    signWellEmbedRef.current = new window.SignWellEmbed({
      url: signUrl,
      containerId: 'contract-wrapper',
      events: {
        completed: _e => {
          options?.onComplete?.();
        },
        closed: _e => {
          options?.onClose?.();
        }
      },
      allowClose: false,
      allowDecline: false
    });

    isContractLoadedRef.current = true;
    signWellEmbedRef.current?.open();
  };

  const closeSignWell = () => {
    if (!isLoaded || !window.SignWellEmbed || !isContractLoadedRef.current) return;
    signWellEmbedRef.current?.close();
  };

  return {
    isScriptLoaded: isLoaded,
    isScriptLoading: isLoading,
    scriptError: error,
    openSignWell,
    closeSignWell
  };
};
